import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Science as ScienceIcon,
} from "@mui/icons-material";
import ReferralsLabTable from "../components/LabPhlebotomyTable";
import { getReferralsList } from "../../specialist-referrals/services/specialistReferralsService";
import { getLocations } from "../../../utils/services/control/masterDataService";

const LabPhlebotomyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Locations from API
  const [locationsList, setLocationsList] = useState([]);

  // Load locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locs = await getLocations({ clinical: true, status: 1 });
        setLocationsList(locs || []);
      } catch (err) {
        console.error("Error loading locations:", err);
      }
    };
    loadLocations();
  }, []);

  // Load referrals from API
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { pageSize: 500 };
      if (selectedLocationId) filters.locationId = selectedLocationId;

      const result = await getReferralsList(filters);
      setReferrals(result.data || []);
    } catch (err) {
      console.error("Error loading referrals:", err);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  // Check for notification from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSnackbar({
        open: true,
        message: location.state.message,
        severity: location.state.severity || "success",
      });
      window.history.replaceState({}, document.title);
    }
    if (location.state?.refresh) {
      loadReferrals();
    }
  }, [location.state, loadReferrals]);

  // Client-side search filter
  const filteredReferrals = useMemo(() => {
    if (!searchText.trim()) return referrals;
    const search = searchText.toLowerCase();
    return referrals.filter(
      (r) =>
        r.patientName?.toLowerCase().includes(search) ||
        r.cipId?.toLowerCase().includes(search) ||
        r.specialistName?.toLowerCase().includes(search)
    );
  }, [referrals, searchText]);

  const handleCreateLab = (referral) => {
    navigate("/LabPhlebotomy/create", { state: { referral } });
  };

  const handleEditLab = (referral) => {
    navigate(`/LabPhlebotomy/edit/${referral.labPhlebotomyId}`);
  };

  const handleViewReferral = (referral) => {
    navigate(`/specialist-referrals/edit/${referral.specialistReferralId}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Build medical centers list from API locations
  const medicalCenters = useMemo(() => {
    return [
      { locationId: "", label: "All Centers" },
      ...locationsList.map((loc) => ({
        locationId: loc.locationId,
        label: loc.medicalCenter,
      })),
    ];
  }, [locationsList]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ScienceIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Lab Phlebotomy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Specialist referrals — create or edit lab phlebotomy records
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by name, CPID, or specialist..."
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {filteredReferrals.length} referral{filteredReferrals.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Sidebar - Medical Centers Filter */}
        <Paper
          variant="outlined"
          sx={{
            width: 220,
            p: 2,
            flexShrink: 0,
            height: "fit-content",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Medical Centers
          </Typography>
          <Stack spacing={1}>
            {medicalCenters.map((center) => (
              <Chip
                key={center.locationId || "all"}
                label={center.label}
                onClick={() => setSelectedLocationId(center.locationId)}
                variant={selectedLocationId === center.locationId ? "filled" : "outlined"}
                color={selectedLocationId === center.locationId ? "primary" : "default"}
                sx={{
                  justifyContent: "flex-start",
                  height: 36,
                  cursor: "pointer",
                  "& .MuiChip-label": {
                    width: "100%",
                  },
                }}
              />
            ))}
          </Stack>
        </Paper>

        {/* Table */}
        <Paper variant="outlined" sx={{ flex: 1, p: 0, overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredReferrals.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <ScienceIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No referrals found
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                {searchText || selectedLocationId
                  ? "Try adjusting your filters"
                  : "No specialist referrals available"}
              </Typography>
            </Box>
          ) : (
            <ReferralsLabTable
              data={filteredReferrals}
              loading={loading}
              onCreateLab={handleCreateLab}
              onEditLab={handleEditLab}
              onViewReferral={handleViewReferral}
            />
          )}
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabPhlebotomyPage;
